// The Contractor Report's Quality Control PDF: every pick QC deducted in the
// report's window, a deduction held until the day closes among them (marked
// provisional), one block each with the picker, the pick time, where it was
// picked on a small map, the quality issue with what it took off, and the
// photos of the quality scan (GET /api/reports/contractor/deductions).
//
// Written by hand like the clock-on card (teams/qrCard.ts), in Helvetica, but
// as bytes: the map and the photos go in as JPEG images, which a string PDF
// cannot carry. Pure: deductionsTask.ts fetches the photos and draws the maps.

import { ENCODING, pdfText, rgb, wrap } from '../teams/qrCard'
import { GPS_FROM_LABEL, type GpsFrom } from '../productActivity/model'
import { dmy } from '../wages/format'

export interface Deduction {
  cycle_id: string
  work_date: string
  contractor: string
  worker_name: string | null
  pick_time: string | null
  picking_job_name: string | null
  site_name: string | null
  location: string | null
  item_code: string
  quality_name: string | null
  qc_factor: number
  lugs_deducted: number
  gps: { lat: number; lng: number } | null
  // The scan had no point: the one shown is the pick's or the receipt's.
  gps_from?: 'quality' | GpsFrom | null
  photos: { record: string; idx: number }[]
  // Held until the day closes: not yet off the credited lugs.
  provisional?: boolean
}

export interface Deductions {
  from: string
  to: string
  contractor: string | null
  items: Deduction[]
}

// A JPEG ready to place: its bytes and its pixel size.
export interface Jpeg {
  bytes: Uint8Array
  w: number
  h: number
}

export interface DeductionImages {
  map: Jpeg | null
  photos: Jpeg[]
  // Photos the store could not hand over.
  missing: number
}

const W = 595
const H = 842
const M = 36
const INK = '#201e1d'
const MUTED = '#6b6560'
const ACCENT = '#b5552b'
const RULE = '#d9d2c7'
const IMG_H = 170
const MAP_W = 230
const MAX_PHOTOS = 3

const pct = (f: number) => `${Math.round((1 - f) * 100)}%`
const lugs = (n: number) => `${n} lug${n === 1 ? '' : 's'}`

export const deductionsPdfName = (d: Deductions) =>
  `quality-control-${d.from}${d.to !== d.from ? `-to-${d.to}` : ''}${d.contractor ? `-${d.contractor.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}` : ''}.pdf`

export function deductionsPdf(d: Deductions, images: DeductionImages[], printedOn: string): Uint8Array<ArrayBuffer> {
  const pages: { ops: string[]; images: string[] }[] = []
  const xobjects: Jpeg[] = []
  let page = { ops: [] as string[], images: [] as string[] }
  let y = H - M
  const newPage = () => {
    page = { ops: [], images: [] }
    pages.push(page)
    y = H - M
  }
  newPage()
  const text = (s: string, x: number, at: number, size: number, bold = false, colour = INK) =>
    page.ops.push(`BT /${bold ? 'F2' : 'F1'} ${size} Tf ${rgb(colour)} rg ${x.toFixed(1)} ${at.toFixed(1)} Td ${pdfText(s)} Tj ET`)
  const rect = (x: number, at: number, w: number, h: number, colour: string) =>
    page.ops.push(`${rgb(colour)} rg ${x.toFixed(1)} ${at.toFixed(1)} ${w.toFixed(1)} ${h.toFixed(1)} re f`)
  const image = (img: Jpeg, x: number, at: number, w: number, h: number) => {
    xobjects.push(img)
    const name = `Im${xobjects.length}`
    page.images.push(name)
    page.ops.push(`q ${w.toFixed(1)} 0 0 ${h.toFixed(1)} ${x.toFixed(1)} ${at.toFixed(1)} cm /${name} Do Q`)
  }

  // The heading: what the file covers.
  text('Quality Control', M, y - 18, 18, true)
  const period = d.from === d.to ? dmy(d.from) : `${dmy(d.from)} to ${dmy(d.to)}`
  text(`Contractor Report · ${period} · ${d.contractor || 'All contractors'}`, M, y - 34, 10, false, MUTED)
  const sum = (xs: Deduction[]) => Math.round(xs.reduce((t, x) => t + x.lugs_deducted, 0) * 100) / 100
  const held = d.items.filter((x) => x.provisional)
  const heldNote = held.length ? `, ${lugs(sum(held))} of it provisional until the day closes` : ''
  text(
    d.items.length
      ? `${d.items.length} deducted pick${d.items.length === 1 ? '' : 's'}, ${lugs(sum(d.items))} deducted${heldNote} · printed ${printedOn}`
      : `No pick was deducted in this period · printed ${printedOn}`,
    M, y - 48, 10, false, MUTED,
  )
  y -= 64

  const blockH = 14 + 13 + 15 + 6 + IMG_H + 22
  d.items.forEach((x, i) => {
    if (y - blockH < M) newPage()
    const im = images[i] || { map: null, photos: [], missing: x.photos.length }
    rect(M, y, W - 2 * M, 0.75, RULE)
    y -= 16
    text(`${x.worker_name || 'Unassigned'} · ${x.contractor}`, M, y, 12, true)
    y -= 13
    const where = [x.picking_job_name, x.site_name, x.location].filter(Boolean).join(' · ')
    const line = `${dmy(x.work_date)} · picked ${x.pick_time || 'at an unknown time'}${where ? ` · ${where}` : ''} · item ${x.item_code}`
    text(wrap(line, 9, W - 2 * M)[0], M, y, 9, false, MUTED)
    y -= 15
    text(`${x.quality_name || 'Quality'} · ${pct(x.qc_factor)} deducted (${lugs(x.lugs_deducted)})${x.provisional ? ' · provisional' : ''}`, M, y, 11, true, ACCENT)
    y -= 6
    const top = y
    // The map, or a box saying why there is none.
    if (im.map) image(im.map, M, top - IMG_H, MAP_W, IMG_H)
    else {
      rect(M, top - IMG_H, MAP_W, IMG_H, '#efebe4')
      text(x.gps ? 'The map could not be loaded' : 'No GPS was recorded for this pick', M + 14, top - IMG_H / 2, 9, false, MUTED)
    }
    if (x.gps) {
      const of = x.gps_from && x.gps_from !== 'quality' ? ` · GPS of ${GPS_FROM_LABEL[x.gps_from]}, none on the QC` : ''
      text(`${x.gps.lat.toFixed(5)}, ${x.gps.lng.toFixed(5)}${of}`, M, top - IMG_H - 11, 8, false, MUTED)
    }
    // The photos side by side at the map's height, all made smaller together
    // where they would not fit beside it.
    let px = M + MAP_W + 10
    const room = W - M - px
    const row = im.photos.slice(0, MAX_PHOTOS)
    const gaps = Math.max(0, row.length - 1) * 8
    const wide = row.reduce((t, p) => t + (p.w / p.h) * IMG_H, 0)
    const k = wide + gaps > room ? (room - gaps) / wide : 1
    for (const p of row) {
      const h = IMG_H * k
      const w = (p.w / p.h) * h
      image(p, px, top - h, w, h)
      px += w + 8
    }
    const shown = row.length
    const notes = [
      ...(!x.photos.length ? ['No photo was taken with this quality scan'] : []),
      ...(im.photos.length > shown ? [`${im.photos.length - shown} more photo${im.photos.length - shown === 1 ? '' : 's'} not shown`] : []),
      ...(im.missing ? [`${im.missing} photo${im.missing === 1 ? '' : 's'} could not be loaded`] : []),
    ]
    if (notes.length) text(notes.join(' · '), M + MAP_W + 10, shown ? top - IMG_H - 11 : top - IMG_H / 2, 8, false, MUTED)
    y = top - IMG_H - 22
  })
  pages.forEach((p, i) => p.ops.push(`BT /F1 8 Tf ${rgb(MUTED)} rg ${W - M - 40} 20 Td ${pdfText(`Page ${i + 1} of ${pages.length}`)} Tj ET`))
  return assemble(pages, xobjects)
}

// The file: catalog, pages, two fonts, then each page and its stream, then the
// images. Every object but an image's stream is ASCII.
function assemble(pages: { ops: string[]; images: string[] }[], images: Jpeg[]): Uint8Array<ArrayBuffer> {
  const enc = new TextEncoder()
  const firstPage = 5
  const firstImage = firstPage + pages.length * 2
  const objects: (string | [string, Uint8Array])[] = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    `<< /Type /Pages /Kids [${pages.map((_, i) => `${firstPage + i * 2} 0 R`).join(' ')}] /Count ${pages.length} >>`,
    `<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding ${ENCODING} >>`,
    `<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding ${ENCODING} >>`,
  ]
  pages.forEach((p, i) => {
    const xo = p.images.map((n) => `/${n} ${firstImage + Number(n.slice(2)) - 1} 0 R`).join(' ')
    const stream = p.ops.join('\n')
    objects.push(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${W} ${H}] /Resources << /Font << /F1 3 0 R /F2 4 0 R >>${xo ? ` /XObject << ${xo} >>` : ''} >> /Contents ${firstPage + i * 2 + 1} 0 R >>`,
      `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`,
    )
  })
  for (const img of images) {
    objects.push([`<< /Type /XObject /Subtype /Image /Width ${img.w} /Height ${img.h} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${img.bytes.length} >>\nstream\n`, img.bytes])
  }
  const parts: Uint8Array[] = []
  let at = 0
  const put = (b: string | Uint8Array) => {
    const bytes = typeof b === 'string' ? enc.encode(b) : b
    parts.push(bytes)
    at += bytes.length
  }
  put('%PDF-1.4\n')
  const offsets: number[] = []
  objects.forEach((body, i) => {
    offsets.push(at)
    put(`${i + 1} 0 obj\n`)
    if (typeof body === 'string') put(body)
    else {
      put(body[0])
      put(body[1])
      put('\nendstream')
    }
    put('\nendobj\n')
  })
  const xref = at
  put(`xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`)
  for (const off of offsets) put(`${String(off).padStart(10, '0')} 00000 n \n`)
  put(`trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`)
  const out = new Uint8Array(at)
  let o = 0
  for (const p of parts) {
    out.set(p, o)
    o += p.length
  }
  return out
}
